import React, { useEffect, useReducer, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardContainer from "../../components/dashboard-container";
import {
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import StorefrontIcon from "@mui/icons-material/Storefront";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import ForecastIcon from "../../globalStyles/icons/forecast-icon.js";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { toast } from "react-toastify";
import { inventoryService } from "../../utils/services/inventoryService";
import { useForecast } from "../../context/forecastContext";
import { useLostSales } from "../../context/lostSalesContext";
import InventoryList from "../../components/inventory-list";
import { inventoryFilterInitialState } from "../../utils/filter-config";
import buildQuery from "../../utils/query-builder";
import ImportIcon from "../../globalStyles/icons/importIcon.js";
import CustomTypography from "../../globalStyles/fonts/index.js";
function filterReducer(state, action) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        columns: state?.columns.map((x) =>
          x?.key === action?.payload?.key ? { ...x, ...action.payload } : x,
        ),
      };
    case "EXISTING_DATA":
      return {
        ...state,
        columns: state.columns.map((x) => ({
          ...x,
          filter:
            action.data.filters.find((y) => y.key === x.key)?.values ||
            x.filter,
        })),
        limit: action.data.limit || state.limit,
      };
    case "SET_OPTIONS":
      return {
        ...state,
        columns: state.columns.map((col) =>
          col.key === action.payload.key
            ? { ...col, options: action.payload.options }
            : col,
        ),
      };
    case "UPDATE_LIMIT":
      return { ...state, limit: action.payload };

    default:
      return state;
  }
}
const Inventory = () => {
  const navigate = useNavigate();
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [selectedInventories, setSelectedInventories] = useState([]);
  const [inventoryList, setInventoryList] = useState([]);
  const [viewMode, setViewMode] = useState("list");

  const client_id = localStorage.getItem("client_id");

  const { openForecast, setForecastDialogOpen, openForecastParamsModal } =
    useForecast();
  const { openLostSalesParamsBulk, bulkParamsLoading } = useLostSales();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [dataCount, setDataCount] = useState(rowsPerPage);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [filterData, dispatch] = useReducer(
    filterReducer,
    inventoryFilterInitialState,
  );

  useEffect(() => {
    let data = localStorage.getItem("inventory-filter-data");
    if (location.search.includes("child") && data) {
      localStorage.removeItem("inventory-filter-data");
      dispatch({ type: "EXISTING_DATA", data: JSON.parse(data) });
    }
  }, []);

  const handleFilterChange = (newFilter) => {
    dispatch({ type: "SET_FILTER", payload: newFilter });
    setPage(0);
  };

  // Kick off a bulk inventory import from the client's OMS provider(s)
  // (e.g. Pipe17 per-location stock -> warehouse inventory via the
  // warehouse-location mapping). Mirrors the Product page's syncProductData.
  const syncInventoryData = async () => {
    setLoading(true);
    try {
      await inventoryService.syncInventory({ client_id });
      toast.success("Inventory import has been successfully initiated!");
    } catch (error) {
      console.error("Inventory import failed:", error);
      toast.error("Failed to start the inventory import");
    } finally {
      setLoading(false);
    }
  };

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const response = await inventoryService.fetch(
        buildQuery(filterData, page),
      );
      setInventoryList(response.data);
      setLoading(false);
      setDataCount(response.total);
    } catch (error) {
      console.error("Fetch failed:", error);
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const response = await inventoryService.fetch(
          buildQuery(filterData, page),
        );
        if (!cancelled) {
          setInventoryList(response.data);
          setLoading(false);
          setDataCount(response.total);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch failed:", error);
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [page, rowsPerPage, filterData]);

  return (
    <>
      <DashboardContainer
        onChangeFilter={handleFilterChange}
        filterButton={false}
        hideUploadButton
        hideAddButton
        header={"Inventory List"}
        setViewMode={setViewMode}
        hideFilterClient={true}
        onClickRefresh={() => {
          fetchInventory();
        }}
        headerButtons={(() => {
          const items = selectedInventories;
          const onForecast = () =>
            openForecastParamsModal(
              selectedInventories,
              {
                onRefresh: fetchInventory,
                onClearSelected: () => setSelectedInventories([]),
              },
              "warehouse",
            );
          const onLostSales = () =>
            openLostSalesParamsBulk(selectedInventories, {
              onClearSelected: () => setSelectedInventories([]),
            });
          return (
            <>
              <Button
                size="small"
                style={{ textTransform: "none", marginRight: 8 }}
                variant="contained"
                onClick={() => {
                  navigate("/inventory-import");
                }}
              >
                <ImportIcon fill="#FFF" />
                <CustomTypography
                  fontSize="14px"
                  fontWeight={500}
                  color="#FFFFFF"
                >
                  Import
                </CustomTypography>
              </Button>
              <Button
                size="small"
                style={{ textTransform: "none" }}
                variant="contained"
                onClick={syncInventoryData}
              >
                <ImportIcon fill="#FFF" />
                <CustomTypography
                  fontSize="14px"
                  fontWeight={500}
                  color="#FFFFFF"
                >
                  Import from OMS
                </CustomTypography>
              </Button>
              {items.length > 0 && (
                <>
                  <Button
                    size="small"
                    style={{ textTransform: "none" }}
                    variant="contained"
                    onClick={onForecast}
                  >
                    <ForecastIcon />
                    <CustomTypography
                      fontSize="14px"
                      fontWeight={500}
                      color="#FFFFFF"
                      fill="#FFFFFF"
                    >
                      Forecast
                    </CustomTypography>
                  </Button>
                  <Button
                    size="small"
                    style={{ textTransform: "none" }}
                    variant="contained"
                    onClick={onLostSales}
                    disabled={bulkParamsLoading}
                    startIcon={<TrendingDownIcon sx={{ color: "#FFF" }} />}
                  >
                    <CustomTypography
                      fontSize="14px"
                      fontWeight={500}
                      color="#FFFFFF"
                    >
                      {bulkParamsLoading ? "Loading..." : "Lost Sales"}
                    </CustomTypography>
                  </Button>
                </>
              )}
            </>
          );
        })()}
      >
        <InventoryList
          onChangeFilter={handleFilterChange}
          filterData={filterData}
          dataCount={dataCount}
          page={page}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={(e) => {
            setRowsPerPage(e);
            dispatch({
              type: "UPDATE_LIMIT",
              payload: e,
            });
          }}
          setPage={setPage}
          data={inventoryList}
          viewMode={viewMode}
          selectedInventory={selectedInventory}
          setSelectedInventory={setSelectedInventory}
          selectedInventories={selectedInventories}
          setSelectedInventories={setSelectedInventories}
          setLoading={setLoading}
          loading={loading}
          fetchInventory={fetchInventory}
          setForecastDialogOpen={setForecastDialogOpen}
          client_id={client_id}
        />
      </DashboardContainer>
    </>
  );
};

export default Inventory;
